import { FC, useState } from "react";
import { Button, Text } from "rizzui";
import { FiPlus, FiImage, FiTrash2 } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { toast } from "sonner";

interface CensusLinePhotosGalleryProps {
    censusLineId?: string;
    photos: File[];
    onPhotosChange: (photos: File[]) => void;
    lineTitle?: string;
}

const CensusLinePhotosGallery: FC<CensusLinePhotosGalleryProps> = ({ 
    censusLineId, 
    photos, 
    onPhotosChange, 
    lineTitle = "Línea" 
}) => {
    
    // ENSURE PHOTOS IS ALWAYS AN ARRAY

    const safePhotos = photos || [];
    
    // STATES

    const [dragActive, setDragActive] = useState(false);

    /**
     * @ES MANEJAR SELECCIÓN DE ARCHIVOS
     * @EN HANDLE FILE SELECTION
     */
    const handleFileSelect = (files: FileList | null) => {
        if (!files) return;

        const validFiles: File[] = [];
        const maxSize = 10 * 1024 * 1024; // 10MB

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} no es una imagen válida`);
                continue;
            }
            
            if (file.size > maxSize) {
                toast.error(`${file.name} es demasiado grande (máximo 10MB)`);
                continue;
            }
            
            validFiles.push(file);
        }

        if (validFiles.length > 0) {
            const newPhotos = [...safePhotos, ...validFiles];
            onPhotosChange(newPhotos);
            toast.success(`${validFiles.length} foto(s) añadida(s)`);
        }
    };

    /**
     * @ES MANEJAR DRAG AND DROP
     * @EN HANDLE DRAG AND DROP
     */
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files);
        }
    };

    /**
     * @ES ELIMINAR FOTO LOCAL
     * @EN DELETE LOCAL PHOTO
     */
    const handleRemoveLocalPhoto = (index: number) => {
        const newPhotos = safePhotos.filter((_, i) => i !== index);
        onPhotosChange(newPhotos);
        toast.success("Foto eliminada");
    };

    /**
     * @ES ELIMINAR TODAS LAS FOTOS LOCALES
     * @EN REMOVE ALL LOCAL PHOTOS
     */
    const handleRemoveAllLocalPhotos = () => {
        onPhotosChange([]);
        toast.success("Todas las fotos eliminadas");
    };

    const totalPhotos = safePhotos.length;

    return (
        <div className="space-y-4"> 
            {/* HEADER */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <FiImage className="h-5 w-5 text-gray-600" />
                    <Text className="font-medium">
                        Fotos de {lineTitle} ({totalPhotos})
                    </Text>
                </div>
                
                {safePhotos.length > 0 && (
                    <Button
                        size="sm"
                        variant="outline"
                        color="danger"
                        onClick={handleRemoveAllLocalPhotos}
                        className="flex items-center gap-2"
                    >
                        <FiTrash2 className="w-4 h-4" />
                        Eliminar nuevas
                    </Button>
                )}
            </div>

            {/* UPLOAD AREA */}
            <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <FiImage className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <Text className="mb-2">
                    Arrastra las fotos aquí o haz clic para seleccionar
                </Text>
                <Text className="text-sm text-gray-500 mb-4">
                    Formatos soportados: JPG, PNG, GIF (máximo 10MB por foto)
                </Text>
                
                <input
                    type="file"
                    id={`photos-upload-${lineTitle}`}
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                />
                
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById(`photos-upload-${lineTitle}`)?.click()}
                    className="flex items-center gap-2 mx-auto"
                >
                    <FiPlus className="w-4 h-4" />
                    Seleccionar fotos
                </Button>
            </div>

            {/* PHOTOS GRID */}
            {totalPhotos > 0 ? (
                <div className="space-y-6">

                    {/* LOCAL PHOTOS */}
                    {safePhotos.length > 0 && (
                        <div>
                            <Text className="text-sm font-medium text-gray-700 mb-3">
                                Fotos nuevas ({safePhotos.length}) - Se guardarán al enviar
                            </Text>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {safePhotos.map((photo, index) => (
                                    <div key={index} className="relative group">
                                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-primary">
                                            <img
                                                src={URL.createObjectURL(photo)}
                                                alt={`Foto ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        
                                        {/* OVERLAY WITH ACTIONS */}
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="solid"
                                                    color="danger"
                                                    onClick={() => handleRemoveLocalPhoto(index)}
                                                    className="p-2"
                                                >
                                                    <MdDeleteOutline className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        {/* PHOTO NAME */}
                                        <Text className="text-xs text-gray-600 mt-1 truncate">
                                            {photo.name}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500">
                    <FiImage className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <Text>No hay fotos para esta línea</Text>
                </div>
            )}
        </div>
    );
};

export default CensusLinePhotosGallery;